import { useEffect, useState } from 'react';
import { getSupabaseRuntimeStatus, verifySupabaseDevRuntime } from './supabaseAdapter';
import { syncTrustedAttemptOutbox } from './outboxSync';
import { useRuntimeOutbox } from './useRuntimeOutbox';
import type { RuntimeBackendStatus, RuntimeVerificationReport } from './types';
import type { RuntimeSyncSummary } from './outboxSync';

const initial: RuntimeBackendStatus = {
  mode: 'local_pilot',
  configured: false,
  authenticated: false,
  realtimeAvailable: false,
  label: 'Memeriksa runtime…',
  detail: 'Status koneksi sedang diperiksa.',
};

export function RuntimeBackendCard() {
  const [status, setStatus] = useState(initial);
  const [verification, setVerification] = useState<RuntimeVerificationReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncSummary, setSyncSummary] = useState<RuntimeSyncSummary | null>(null);
  const outbox = useRuntimeOutbox();
  const pending = outbox.filter((event) => event.status === 'pending' || event.status === 'syncing' || event.status === 'failed').length;

  useEffect(() => {
    let mounted = true;
    void getSupabaseRuntimeStatus().then((next) => { if (mounted) setStatus(next); });
    return () => { mounted = false; };
  }, []);

  async function runVerification() {
    setBusy(true);
    try {
      setVerification(await verifySupabaseDevRuntime());
      setStatus(await getSupabaseRuntimeStatus());
    } finally {
      setBusy(false);
    }
  }

  async function runSync() {
    setBusy(true);
    try {
      setSyncSummary(await syncTrustedAttemptOutbox());
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card runtime-card">
      <div>
        <div className="eyebrow">Runtime Integration</div>
        <h2>{status.label}</h2>
        <p className="muted">{status.detail}</p>
      </div>
      <div className="grid grid--3">
        <div className="metric"><span>Mode</span><strong>{status.mode === 'supabase' ? 'Cloud' : 'Local'}</strong></div>
        <div className="metric"><span>Realtime</span><strong>{status.realtimeAvailable ? 'Siap uji' : 'Belum'}</strong></div>
        <div className="metric"><span>Menunggu sync</span><strong>{pending}</strong></div>
      </div>

      <div className="runtime-actions">
        <button type="button" className="button button--secondary" onClick={runVerification} disabled={busy}>
          {busy ? 'Memeriksa…' : 'Periksa Supabase DEV'}
        </button>
        <button type="button" className="button button--secondary" onClick={runSync} disabled={busy || !status.authenticated}>
          Sinkronkan event siap-cloud
        </button>
      </div>

      {syncSummary ? (
        <p className="muted runtime-note">Sync: {syncSummary.synced} berhasil, {syncSummary.failed} gagal, {syncSummary.deferred} menunggu retry, {syncSummary.skipped} local-only{syncSummary.offline ? ' · perangkat sedang offline' : ''}.</p>
      ) : null}

      {verification ? (
        <div className="runtime-checks">
          <strong>{verification.readyForLivePilot ? 'DEV runtime siap untuk pilot cloud.' : 'DEV runtime belum siap penuh.'}</strong>
          <ul>
            {verification.checks.map((check) => (
              <li key={check.key}>
                <span>{check.status === 'pass' ? '✓' : check.status === 'fail' ? '✕' : '•'} {check.label}</span>
                <small>{check.detail}</small>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <p className="muted runtime-note">
        Event belajar tetap disimpan lokal terlebih dahulu. Hanya assignment cloud yang mempunyai UUID frozen manifest yang dapat dikirim ke trusted grading RPC; demo local tidak akan dikirim secara keliru.
      </p>
    </section>
  );
}
